/**
 * Stable ids for the default cards. The card `id` field is a free string now
 * (admins can add their own), so this is just the starter set — used for
 * legacy click-behavior fallback lookups and back-compat with AppData stored
 * before card management existed.
 */
export type TrialHomeCardId =
  | 'condition-reports'
  | 'clinical-protocols'
  | 'pcp-reports'
  | 'soap-notes'
  | 'scheduling'
  | 'billing';

/**
 * Curated icon names admins pick from. The chiroup app maps each name to a
 * concrete heroicon — adding an option here means also adding the matching
 * entry in `apps/chiroup/src/components/home/trial/cardIcons.ts` (and the
 * admin preview mirror).
 */
export type TrialCardIcon =
  | 'clipboard-check'
  | 'book-open'
  | 'paper-airplane'
  | 'document-text'
  | 'calendar'
  | 'banknotes'
  | 'sparkles'
  | 'chart-bar'
  | 'rocket'
  | 'megaphone'
  | 'puzzle'
  | 'video-camera';

export type TrialCardSection = 'essentials' | 'ehr';

/**
 * What happens when the user clicks a card. Marketing/admin picks the kind in
 * the Trial Home editor; the chiroup app routes the click accordingly.
 *
 *  - `tour`   → start the in-app tour with this id (must exist in the tour
 *               registry; admin picks from a dropdown populated by it).
 *  - `video`    → open a modal embedding the URL (YouTube/Loom/Vimeo).
 *  - `route`    → navigate to this in-app path.
 *  - `external` → open this URL in a new browser tab (any external site).
 *  - `none`     → no-op (card is informational only).
 */
export type ClickBehavior =
  | { kind: 'tour'; tourId: string }
  | { kind: 'video'; url: string; title?: string }
  | { kind: 'route'; path: string }
  | { kind: 'external'; url: string }
  | { kind: 'none' };

export type TrialHomeCard = {
  /** Stable id. Default cards use the {@link TrialHomeCardId} values; admin-
   *  added cards get a generated `custom-…` slug. Must be unique within the
   *  cards array. */
  id: string;
  enabled: boolean;
  title: string;
  description: string;
  /** Which section the card renders under. Order within the section comes
   *  from the cards array's stored order. */
  section: TrialCardSection;
  /** Curated icon name resolved by {@link TrialCardIcon} in the chiroup app. */
  icon: TrialCardIcon;
  /** Optional small pill badge in the top-right of the card (e.g., "Start here"). */
  badge?: string;
  /** What happens when the card is clicked. Falls back to the legacy
   *  per-card-id behavior when undefined, so existing stored content keeps
   *  working without a backfill. */
  clickBehavior?: ClickBehavior;
};

export type TrialHomeContent = {
  heading: string;
  essentialsLabel: string;
  ehrLabel: string;
  cards: TrialHomeCard[];
  ctaTitle: string;
  ctaButton: string;
  /** Small fine-print line under the CTA button. */
  ctaSubtitle?: string;
  /** Heading shown on the "Kickstart" progress hero at the top of the page. */
  kickstartHeading?: string;
  /** Testimonial / quote shown at the bottom of the Kickstart hero. */
  kickstartTestimonial?: string;
  /** Whether the testimonial card renders at all (admin can hide it). */
  kickstartTestimonialEnabled?: boolean;
  kickstartTestimonialAuthor?: string;
  /** Avatar image URL (S3, uploaded via the admin Uploader). */
  kickstartTestimonialAvatar?: string;
  /** Star rating, 0–5. */
  kickstartTestimonialRating?: number;
  /** Scheduler link (ChiliPiper) embedded by the trial CTAs. Used for trial
   *  clinics and as the fallback for any paid tier without its own link. */
  scheduleCallUrl?: string;
  /** Per-tier scheduler overrides for paid Essentials clinics on the TTFV home.
   *  When set, that plan uses this link instead of {@link scheduleCallUrl};
   *  blank falls back to scheduleCallUrl. Lets onboarders route Basic/Plus/
   *  Premium signups to a different (e.g. round-robin) booking link. */
  scheduleCallUrlBasic?: string;
  scheduleCallUrlPlus?: string;
  scheduleCallUrlPremium?: string;
  /** Checklist "bonus step" — admin-customizable schedule-a-call row with an
   *  expert photo. Opens the scheduler (scheduleCallUrl). */
  bonusStepEnabled?: boolean;
  bonusStepTitle?: string;
  bonusStepSubtitle?: string;
  /** Expert photo URL (S3, uploaded via the admin Uploader). */
  bonusStepImage?: string;
  /** Pill badge on the bonus-step card (e.g., "Fast Track"). */
  bonusStepBadge?: string;
  /** Label for the bonus-step call-to-action button. */
  bonusStepButtonText?: string;
  /** Checklist "Select my plan" CTA — admin-editable copy; starts Essentials checkout. */
  selectPlanEnabled?: boolean;
  selectPlanTitle?: string;
  selectPlanSubtitle?: string;
};

export const DEFAULT_SCHEDULE_CALL_URL =
  'https://chiroup.chilipiper.com/round-robin/chiroup-walkthrough';

/**
 * Resolve the schedule-a-call scheduler link for a clinic, honoring per-tier
 * overrides. Trials (and any plan without a tier link) use the base
 * `scheduleCallUrl`; Basic/Plus/Premium use their own link when set, otherwise
 * fall back to `scheduleCallUrl`, then the hardcoded default.
 */
export const resolveScheduleCallUrl = (
  content: Pick<
    TrialHomeContent,
    'scheduleCallUrl' | 'scheduleCallUrlBasic' | 'scheduleCallUrlPlus' | 'scheduleCallUrlPremium'
  >,
  plan?: string | null
): string => {
  const perTier =
    plan === 'basic'
      ? content.scheduleCallUrlBasic
      : plan === 'plus'
        ? content.scheduleCallUrlPlus
        : plan === 'premium'
          ? content.scheduleCallUrlPremium
          : undefined;

  return perTier || content.scheduleCallUrl || DEFAULT_SCHEDULE_CALL_URL;
};

export const DEFAULT_TRIAL_HOME: TrialHomeContent = {
  heading: 'What would you like to explore first?',
  essentialsLabel: 'Use these tools in your clinic today',
  ehrLabel: 'See how everything works together',
  cards: [
    {
      id: 'condition-reports',
      enabled: true,
      section: 'essentials',
      icon: 'clipboard-check',
      title: 'Condition Reports',
      description: 'Patient-ready report of findings with exercises in seconds',
      badge: 'Start here',
      clickBehavior: { kind: 'tour', tourId: 'condition-reports' },
    },
    {
      id: 'pcp-reports',
      enabled: true,
      section: 'essentials',
      icon: 'paper-airplane',
      title: 'PCP Reports',
      description: 'Generate clear, referral-ready reports in seconds',
      clickBehavior: { kind: 'tour', tourId: 'pcp-reports' },
    },
    {
      id: 'clinical-protocols',
      enabled: true,
      section: 'essentials',
      icon: 'book-open',
      title: 'Clinical Protocols',
      description: 'Your clinical GPS with step-by-step guidance and videos',
      clickBehavior: { kind: 'tour', tourId: 'clinical-protocols' },
    },
    {
      id: 'soap-notes',
      enabled: true,
      section: 'ehr',
      icon: 'document-text',
      title: 'SOAP Notes',
      description: 'Lightning-fast, meaningful notes in seconds',
      clickBehavior: { kind: 'route', path: '/patients' },
    },
    {
      id: 'scheduling',
      enabled: true,
      section: 'ehr',
      icon: 'calendar',
      title: 'Scheduling',
      description: 'Keep your day full and flowing without the friction',
      clickBehavior: { kind: 'route', path: '/schedule' },
    },
    {
      id: 'billing',
      enabled: true,
      section: 'ehr',
      icon: 'banknotes',
      title: 'Billing',
      description: 'Capture revenue cleanly with fewer errors and less work',
      clickBehavior: { kind: 'route', path: '/billing' },
    },
  ],
  ctaTitle: 'Want to see how this fits your clinic?',
  ctaButton: 'Guided Walkthrough',
  ctaSubtitle: 'Tailored to your clinic. No sales pitch, no commitment.',
  kickstartHeading: 'Kickstart Your ChiroUp Experience',
  kickstartTestimonial:
    'Fantastic system for patient education and exercises, practice building and clinician skill building. I highly recommend this for both new and established doctors!',
  kickstartTestimonialEnabled: true,
  kickstartTestimonialAuthor: '',
  kickstartTestimonialAvatar: '',
  kickstartTestimonialRating: 5,
  scheduleCallUrl: DEFAULT_SCHEDULE_CALL_URL,
  bonusStepEnabled: true,
  bonusStepTitle: 'Schedule a call with one of our platform experts!',
  bonusStepSubtitle: 'Get a personalized walkthrough tailored to your clinic.',
  bonusStepImage: '',
  bonusStepBadge: 'Fast Track',
  bonusStepButtonText: 'Book my 15 minutes',
  selectPlanEnabled: true,
  selectPlanTitle: 'Select my plan',
  selectPlanSubtitle: 'Ready to go? Start your Essentials subscription.',
};

/**
 * Tours marketing can pick when configuring a card's {@link ClickBehavior}.
 * Add an entry whenever a new tour ships — the id must match `Tour.id`.
 * Kept here (not apps/chiroup) so the admin editor renders the dropdown
 * without importing from another app.
 */
export const TRIAL_TOUR_OPTIONS: { id: string; label: string }[] = [
  { id: 'condition-reports', label: 'Condition Reports walkthrough' },
  { id: 'clinical-protocols', label: 'Clinical Protocols walkthrough' },
  { id: 'patient-create', label: 'Create a patient walkthrough' },
  { id: 'pcp-reports', label: 'PCP Reports walkthrough' },
];

/**
 * One editable step's worth of copy (no anchors / placement / types — those
 * stay code-owned). Used both as the source of truth for the admin Trial
 * Tours editor AND as the default text the chiroup tour engine renders when
 * no admin override is saved.
 */
export type TrialTourStepManifest = {
  stepKey: string;
  /** Optional short hint shown to admin so they know which step this is.
   *  Not user-facing — pure editor metadata. */
  hint?: string;
  title?: string;
  body?: string;
  proTip?: string;
  nextLabel?: string;
  /** Video (YouTube / Loom / Vimeo share or embed URL) shown inside the step.
   *  Admin-editable; falls back to the code default in the tour file. */
  video?: string;
};

export type TrialTourManifest = {
  id: string;
  label: string;
  /** Editor-only short note describing what the tour walks through. */
  hint?: string;
  steps: TrialTourStepManifest[];
};

/**
 * Canonical text content for every trial tour step. Marketing's
 * `trialTourOverrides` AppData layer (see TrialTourOverrides in
 * apps/chiroup/.../tour/types.ts) overlays this — undefined fields fall back
 * to whatever's here.
 *
 * IMPORTANT: stepKey values must match exactly with the `stepKey` field on
 * the corresponding tour step in apps/chiroup/src/components/tour/tours/*.
 * Renaming a stepKey is a breaking change — any saved overrides keyed to
 * the old name become orphans.
 */
export const TRIAL_TOUR_MANIFESTS: TrialTourManifest[] = [
  {
    id: 'condition-reports',
    label: 'Condition Reports walkthrough',
    hint: 'Walks a trial user through creating their first condition report.',
    steps: [
      {
        stepKey: 'intro-video',
        hint: 'Centered intro with an embedded video and a "Build One Now!" button. Paste a YouTube / Loom / Vimeo share link in the Video field.',
        title: 'Condition Reports is your sidekick to better patient outcomes.',
        video: 'https://www.loom.com/share/133d31779800446e948f13457be5cd1c',
        nextLabel: 'Build One Now!',
      },
      {
        stepKey: 'click-rx-button',
        hint: 'Highlights the Rx (quick-start) icon in the top nav.',
        title: 'Rx a Condition Report',
        body: 'Click the quick start "Rx button" to open up the report builder!',
        proTip: "Pro-tip: The Rx button can also be accessed in your patient's record.",
      },
      {
        stepKey: 'pick-patient',
        hint: 'On /report — patient search field. ReportPatient.tsx pre-fills the autocomplete with "sample" when this step lands, so the Sample row shows up in the dropdown on first click. Auto-advances when a patient is picked (URL change).',
        title: 'Pick the patient',
        body: "Click the search above — we've pre-filled it with our sample patient so you can pick it in one tap. You can clear the search to find an existing patient or add a new one.",
        proTip: 'Pro-tip: You can replay this walkthrough any time from your trial home.',
        nextLabel: 'Try it now!',
      },
      {
        stepKey: 'pick-condition',
        hint: 'Condition selector combobox. Auto-advances the moment a condition is picked.',
        title: 'Pick the condition',
        body: "Now you're ready to build your report! 🎉 Select the patient's condition to get started.",
        proTip:
          'This will automatically populate best practices that you can review and customize.',
        nextLabel: 'Got it!',
      },
      {
        stepKey: 'click-save',
        hint: 'Save (floppy disk) button — action step, advances after save lands.',
        title: 'Now just Review & Save!',
        proTip:
          'Pro tip: RX reports were designed to be customizable! After you review and make adjustments, you can click save to preview & deliver your final report.',
      },
      {
        stepKey: 'outro',
        hint: 'Centered modal — fires once the save is complete. Clicking the Next button (label: "Personalize my reports") hands directly into the Personalize modal via PersonalizeReportsAutoTrigger.',
        title: "🎉 You're off and running!",
        body: "You just saved your first Condition Report. Now let's make every report look like yours — add your clinic name, logo, and brand color in 60 seconds.",
        proTip: 'Reports go out cleaner and more professional once your branding is in.',
        nextLabel: 'Personalize my reports',
      },
    ],
  },
  {
    id: 'clinical-protocols',
    label: 'Clinical Protocols walkthrough',
    hint: 'Walks a trial user from the sidenav into the protocol library and a condition detail page.',
    steps: [
      {
        stepKey: 'open-clinical-skills',
        hint: 'Highlights the Clinical Skills item in the left nav.',
        title: 'Open Clinical Skills',
        body: 'Click the Clinical Skills tab in the left nav to find the protocol library.',
      },
      {
        stepKey: 'open-condition-reference',
        hint: 'Condition Reference sub-nav item — click navigates.',
        title: 'Condition Reference',
        body: 'Open Condition Reference to browse the protocol library.',
      },
      {
        stepKey: 'browse-conditions',
        hint: 'On /clinical-skills/conditions — list area highlighted.',
        title: 'Evidence-based protocols for 200+ conditions',
        body: 'Select any condition to access up-to-date, best-practice clinical protocols with tutorial videos covering the most pertinent assessments, treatments, and rehab strategies.',
        proTip: 'Available anytime from the Clinical Skills tab in the left menu.',
        nextLabel: 'Pick a condition',
      },
      {
        stepKey: 'explore-tabs',
        hint: 'Condition detail page — tabs row highlighted. Body supports `**bold**` markdown (parsed by BodyParagraphs); each line leads with the tab name in bold so the three tabs scan at a glance.',
        title: 'Explore the protocol',
        body: '**Plan** — evidence-based treatments and rehab progressions for this condition.\n\n**Objective** — key orthopedic tests, assessments, and findings.\n\n**Lit Review** — latest clinical references and video tutorials our advisors curate.',
        nextLabel: 'Got it!',
      },
      {
        stepKey: 'outro',
        hint: 'Centered closing modal.',
        title: "🎉 You're set",
        body: 'Clinical Protocols is your in-clinic reference. Open it anytime from the left nav whenever you need a refresher on assessments, treatments, or evidence.',
        nextLabel: 'Done',
      },
    ],
  },
  {
    id: 'patient-create',
    label: 'Create a patient walkthrough',
    hint: 'Two-step shortcut for adding a patient via the navbar + icon.',
    steps: [
      {
        stepKey: 'intro',
        hint: 'Centered intro before highlighting the navbar.',
        title: 'Adding a Patient',
        body: "We'll show you the two-step shortcut to add a patient — takes about 30 seconds.",
        nextLabel: 'Show me',
      },
      {
        stepKey: 'click-plus',
        hint: 'The + icon next to ⌘K in the search bar.',
        title: 'Adding a Patient',
        body: 'Use this shortcut to quickly add a patient to your database! 🚀',
      },
      {
        stepKey: 'fill-form',
        hint: 'Patient create modal — anchored on the first name input. No Next button; the tour auto-advances to the outro when the modal navigates to /patients/{id}.',
        title: 'Adding a Patient',
        body: 'Fill in the required (*) information for the patient and click Create.',
      },
      {
        stepKey: 'outro',
        hint: 'Centered celebration on the patient detail page after the create succeeds. Use `**bold**` markdown if you want to highlight a word (e.g., **Rx**).',
        title: '🎉 You added your first patient!',
        body: "Congratulations — that's all it takes. You can now create reports for this patient via the **Rx** icon, or access their chart anytime from the search bar.",
        nextLabel: 'Done',
      },
    ],
  },
  {
    id: 'pcp-reports',
    label: 'PCP Reports walkthrough',
    hint: 'Walks a trial user through generating a referring-provider letter inside the report builder.',
    steps: [
      {
        stepKey: 'click-rx-button',
        hint: 'Highlights the Rx (quick-start) icon in the top nav — same anchor as Condition Reports.',
        title: 'Send a PCP Report',
        body: 'A PCP Report is a clean, branded letter to a patient\'s referring provider — built from the same report you\'d already create. Start by clicking the "Rx" button.',
        proTip: 'Same starting point as a Condition Report — you choose how to deliver it.',
      },
      {
        stepKey: 'pick-patient',
        hint: 'On /report — patient search field. Shared anchor with Condition Reports; ReportPatient.tsx pre-fills the autocomplete with "sample" when this step lands so the Sample row appears in the dropdown.',
        title: 'Pick the patient',
        body: "Click the search above — we've pre-filled it with our sample patient so you can pick it in one tap. You can clear the search to find an existing patient or add a new one.",
        proTip: 'Pro-tip: You can replay this walkthrough any time from your trial home.',
        nextLabel: 'Try it now!',
      },
      {
        stepKey: 'pick-condition',
        hint: 'Condition selector combobox. Shared anchor with Condition Reports.',
        title: 'Pick the condition',
        body: "Choose the patient's condition. The report's findings, exercises, and PCP letter will all pull from this.",
        proTip:
          'This populates evidence-based defaults that you can review and customize before sending.',
        nextLabel: 'Got it!',
      },
      {
        stepKey: 'scroll-to-provider',
        hint: 'Highlights the Healthcare provider section header — auto-scrolls into view.',
        title: 'Scroll down to the referring provider',
        body: "A little further down the report builder you'll find the Healthcare provider section. This is where the PCP report happens.",
        nextLabel: 'Show me',
      },
      {
        stepKey: 'pick-provider',
        hint: 'Healthcare provider autocomplete inside ReportHealthcareProvider.',
        title: "Pick the patient's PCP",
        body: 'Search the directory or add a new one. The letter is automatically addressed to whoever you select here.',
        proTip: 'Saved providers are stored per clinic, so you only have to add each PCP once.',
        nextLabel: 'Got it!',
      },
      {
        stepKey: 'preview-summary',
        hint: '"Preview summary" button — action step, advances on click.',
        title: 'Generate the summary',
        body: 'Click "Preview summary" and we\'ll draft a referral-ready letter using the patient and condition you picked.',
        proTip: 'You can edit every word before saving — the draft is just a starting point.',
      },
      {
        stepKey: 'click-save',
        hint: 'Save (floppy disk) button — same anchor as Condition Reports. Tour ends on save; a bottom-right PCP nudge card takes over from here.',
        title: 'Save the report',
        body: 'Then click save to finalize the report and lock in the PCP letter.',
        proTip: 'Saving makes the report available to print, email, and edit later.',
      },
    ],
  },
];

/**
 * Single source of truth for hydrating stored Trial Home content. Both the
 * admin editor and the chiroup-side hook call this so they can't diverge — a
 * new field on {@link TrialHomeCard}/{@link TrialHomeContent} just needs a
 * fallback added here.
 *
 * Stored card order is preserved verbatim and admin-added custom cards survive
 * (we don't iterate `DEFAULT_TRIAL_HOME.cards` and drop unknowns). Cards
 * missing newer fields get safe fallbacks instead of `undefined`.
 */
export function mergeTrialHomeWithDefaults(
  stored: Partial<TrialHomeContent> | null | undefined
): TrialHomeContent {
  if (!stored) return DEFAULT_TRIAL_HOME;
  const defaultsById = new Map(DEFAULT_TRIAL_HOME.cards.map(c => [c.id, c]));

  const cards: TrialHomeCard[] = stored.cards?.length
    ? stored.cards.map(c => {
        const def = defaultsById.get(c.id);
        const merged = { ...(def ?? {}), ...c } as Partial<TrialHomeCard>;

        return {
          id: merged.id ?? c.id,
          enabled: merged.enabled ?? true,
          title: merged.title ?? 'Untitled',
          description: merged.description ?? '',
          // Default section is 'essentials' so legacy cards without a stored
          // section don't vanish (both filters in TrialHome require it).
          section: merged.section ?? 'essentials',
          icon: merged.icon ?? 'sparkles',
          badge: merged.badge,
          clickBehavior: merged.clickBehavior,
        };
      })
    : DEFAULT_TRIAL_HOME.cards;

  return {
    ...DEFAULT_TRIAL_HOME,
    ...stored,
    cards,
  };
}

/**
 * Human-readable labels for the curated icon set. Used by the admin picker;
 * the chiroup app uses the same string keys to resolve concrete components.
 */
export const TRIAL_CARD_ICON_OPTIONS: { id: TrialCardIcon; label: string }[] = [
  { id: 'clipboard-check', label: 'Clipboard (check)' },
  { id: 'book-open', label: 'Book' },
  { id: 'paper-airplane', label: 'Paper airplane' },
  { id: 'document-text', label: 'Document' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'banknotes', label: 'Banknotes' },
  { id: 'sparkles', label: 'Sparkles' },
  { id: 'chart-bar', label: 'Chart' },
  { id: 'rocket', label: 'Rocket' },
  { id: 'megaphone', label: 'Megaphone' },
  { id: 'puzzle', label: 'Puzzle piece' },
  { id: 'video-camera', label: 'Video camera' },
];
